import React, { useEffect, useState } from 'react'

import {
  Box,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  Card,
} from '@material-ui/core'
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@material-ui/icons'

import certificadoFormal from 'images/certificados/certificado_formal.png'
import certificadoMinimalista from 'images/certificados/certificado_minimalista.png'
import certificadoPadrao from 'images/certificados/certificado_padrao.png'
import { JoditEditor, LoadingFeedback } from 'components'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'
import useStyles from './styles'

const extractFilename = (url) => {
  if (!url) return ''
  return url.split('/').pop()?.split('?')[0] ?? ''
}

const TEMPLATE_LABELS = {
  modelo_padrao: 'Modelo Padrão',
  modelo_formal: 'Modelo Formal',
  modelo_minimalista: 'Modelo Minimalista',
}

const TEMPLATE_SVGS = {
  modelo_padrao: certificadoPadrao,
  modelo_formal: certificadoFormal,
  modelo_minimalista: certificadoMinimalista,
}

const TEMPLATE_LAYOUTS = {
  modelo_padrao: {
    logoTop: '6%',
    logoLeft: '50%',
    logoTransform: 'translateX(-50%)',
    contentLeft: '15%',
    contentRight: '15%',
    titleAlign: 'center',
  },
  modelo_formal: {
    logoTop: '6%',
    logoLeft: '50%',
    logoTransform: 'translateX(-50%)',
    contentLeft: '15%',
    contentRight: '15%',
    titleAlign: 'center',
  },
  modelo_minimalista: {
    logoTop: '5%',
    logoRight: '8%',
    contentLeft: '14%',
    contentRight: '13%',
    titleAlign: 'left',
  },
}

const StepPreviewCertificate = ({
  courseId,
  courseCompanyConfig,
  isCourseCompanyConfigLoading,
  refreshCourseCompanyConfig,
  isEdit,
}) => {
  const classes = useStyles()
  const snackbar = useSnackbar()

  const { response: certResponse, isLoading: isCertLoading } = useFetch(
    service.dponet.trainingCourse.certificateConfig,
    { courseId },
    [courseId],
  )

  const certificateConfig = certResponse?.data?.certificateConfig ?? {}

  const layout =
    TEMPLATE_LAYOUTS[certificateConfig?.template] ??
    TEMPLATE_LAYOUTS.modelo_padrao
  const primaryColor = certificateConfig?.primaryColor || '#233154'
  const secondaryColor = certificateConfig?.secondaryColor || '#ffffff'

  const [hideSignature, setHideSignature] = useState(false)
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null)

  useEffect(() => {
    setHideSignature(courseCompanyConfig?.hideSignature ?? false)
  }, [courseCompanyConfig?.hideSignature])

  const handleToggleHideSignature = async (event) => {
    const value = event.target.checked
    setHideSignature(value)
    try {
      await service.dponet.trainingCourse.upsertCourseCompanyConfig({
        courseId,
        hide_signature: value,
      })
      refreshCourseCompanyConfig()
      snackbar.open({
        message: 'Configuração de assinatura salva com sucesso.',
        variant: 'success',
      })
    } catch {
      snackbar.open({
        message: 'Erro ao salvar configuração de assinatura.',
        variant: 'error',
      })
      setHideSignature(!value)
    }
  }

  return (
    <Card>
      <Box p={2}>
        <LoadingFeedback open={isCertLoading || isCourseCompanyConfigLoading} />
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant="h4">
            Certificado
          </Typography>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Modelo de certificado"
                    value={TEMPLATE_LABELS[certificateConfig?.template] ?? ''}
                    variant="outlined"
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Logo"
                    value={extractFilename(certificateConfig?.logoUrl)}
                    variant="outlined"
                    fullWidth
                    disabled
                    InputProps={{
                      endAdornment: certificateConfig?.logoUrl && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setImagePreviewUrl(certificateConfig.logoUrl)
                            }
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Título"
                    value={certificateConfig?.title ?? ''}
                    variant="outlined"
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Breve descrição do conteúdo do curso ou objetivo principal"
                    value={certificateConfig?.shortDescription ?? ''}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={5}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography className={classes.editorLabel}>
                    Descrição da competência adquirida
                  </Typography>
                  <JoditEditor.Editor
                    value={certificateConfig?.competencyDescription ?? ''}
                    onChange={() => {}}
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                className={classes.certificatePreviewWrapper}
                style={{
                  backgroundColor: secondaryColor,
                  containerType: 'inline-size',
                }}
              >
                <img
                  src={
                    TEMPLATE_SVGS[certificateConfig?.template] ??
                    certificadoPadrao
                  }
                  alt="Preview do certificado"
                  className={classes.certificatePreview}
                />

                {/* Logo placeholder */}
                <Box
                  style={{
                    position: 'absolute',
                    top: layout.logoTop,
                    ...(layout.logoLeft !== undefined && {
                      left: layout.logoLeft,
                    }),
                    ...(layout.logoRight !== undefined && {
                      right: layout.logoRight,
                    }),
                    ...(layout.logoTransform && {
                      transform: layout.logoTransform,
                    }),
                    border: `1.5px dashed ${primaryColor}55`,
                    borderRadius: '0.5cqw',
                    paddingLeft: '1.5cqw',
                    paddingRight: '1.5cqw',
                    paddingTop: '0.6cqw',
                    paddingBottom: '0.6cqw',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${secondaryColor}CC`,
                  }}
                >
                  <span
                    style={{
                      fontSize: '1.6cqw',
                      color: primaryColor,
                      opacity: 0.5,
                      lineHeight: 1,
                    }}
                  >
                    logo aqui
                  </span>
                </Box>

                {/* Content layer */}
                <Box
                  style={{
                    position: 'absolute',
                    top: '22%',
                    left: layout.contentLeft,
                    right: layout.contentRight,
                    bottom: '5%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.6cqw',
                    overflow: 'hidden',
                    lineHeight: 'normal',
                  }}
                >
                  <Box
                    style={{
                      fontSize: '3.8cqw',
                      fontWeight: 800,
                      color: primaryColor,
                      textAlign: layout.titleAlign,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      lineHeight: 1.1,
                      flexShrink: 0,
                    }}
                  >
                    {certificateConfig?.title || 'Certificado de Conclusão'}
                  </Box>

                  <Box
                    style={{
                      fontSize: '1.65cqw',
                      color: primaryColor,
                      lineHeight: 1.45,
                      flexShrink: 1,
                      overflow: 'hidden',
                    }}
                  >
                    {certificateConfig?.shortDescription || ''}
                  </Box>

                  {certificateConfig?.competencyDescription && (
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: certificateConfig.competencyDescription,
                      }}
                      style={{
                        fontSize: '1.65cqw',
                        color: primaryColor,
                        lineHeight: 1.4,
                        flexShrink: 1,
                        overflow: 'hidden',
                      }}
                    />
                  )}

                  <Box
                    style={{
                      marginTop: 'auto',
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5cqw',
                    }}
                  >
                    <Box
                      style={{
                        border: `1px dashed ${primaryColor}45`,
                        borderRadius: '0.4cqw',
                        paddingLeft: '2cqw',
                        paddingRight: '2cqw',
                        paddingTop: '0.4cqw',
                        paddingBottom: '0.4cqw',
                        fontSize: '1.3cqw',
                        color: primaryColor,
                        opacity: 0.45,
                        lineHeight: 1,
                      }}
                    >
                      assinatura aqui
                    </Box>
                    <Box
                      style={{
                        fontSize: '1.6cqw',
                        color: primaryColor,
                        textAlign: 'center',
                        lineHeight: 1.3,
                      }}
                    >
                      {certificateConfig?.signerName || 'Nome do Signatário'}
                      {' — '}
                      {certificateConfig?.signerRole || 'Cargo'}
                    </Box>
                    <Box
                      style={{
                        fontSize: '1.3cqw',
                        color: primaryColor,
                        opacity: 0.6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5cqw',
                        lineHeight: 1,
                      }}
                    >
                      Realização:
                      <span
                        style={{
                          border: `1px dashed ${primaryColor}40`,
                          borderRadius: '0.3cqw',
                          paddingLeft: '0.5cqw',
                          paddingRight: '0.5cqw',
                          paddingTop: '0.15cqw',
                          paddingBottom: '0.15cqw',
                        }}
                      >
                        logo aqui
                      </span>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant="h4">
            Assinatura
          </Typography>
          <FormControlLabel
            className={classes.signatureToggle}
            label="Ocultar Assinatura"
            labelPlacement="start"
            control={
              <Switch
                disabled={!isEdit}
                checked={hideSignature}
                onChange={handleToggleHideSignature}
                color="primary"
              />
            }
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome"
                value={certificateConfig?.signerName ?? ''}
                variant="outlined"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Cargo"
                value={certificateConfig?.signerRole ?? ''}
                variant="outlined"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Assinatura"
                value={extractFilename(certificateConfig?.signatureUrl)}
                variant="outlined"
                fullWidth
                disabled
                InputProps={{
                  endAdornment: certificateConfig?.signatureUrl && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setImagePreviewUrl(certificateConfig.signatureUrl)
                        }
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle} variant="h4">
            Cores
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box className={classes.colorField}>
                <Box
                  className={classes.colorSwatch}
                  style={{ backgroundColor: certificateConfig?.primaryColor }}
                />
                <TextField
                  label="Cor Primária (Textos)"
                  value={certificateConfig?.primaryColor ?? ''}
                  variant="outlined"
                  fullWidth
                  disabled
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className={classes.colorField}>
                <Box
                  className={classes.colorSwatch}
                  style={{ backgroundColor: certificateConfig?.secondaryColor }}
                />
                <TextField
                  label="Cor Secundária (Fundo)"
                  value={certificateConfig?.secondaryColor ?? ''}
                  variant="outlined"
                  fullWidth
                  disabled
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Dialog
          open={Boolean(imagePreviewUrl)}
          onClose={() => setImagePreviewUrl(null)}
          maxWidth="md"
          fullWidth
        >
          <Box className={classes.dialogHeader}>
            <IconButton size="small" onClick={() => setImagePreviewUrl(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent className={classes.dialogContent}>
            <img
              src={imagePreviewUrl}
              alt="Visualização"
              className={classes.previewImage}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </Card>
  )
}

export default StepPreviewCertificate
